import type { NextEditorPlugin, OptionalPlugin } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import type { ContextIdentifierPlugin } from '@atlaskit/editor-plugin-context-identifier';
import type { DecorationsPlugin } from '@atlaskit/editor-plugin-decorations';
import type { FloatingToolbarPlugin } from '@atlaskit/editor-plugin-floating-toolbar';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection';
import type { WidthPlugin } from '@atlaskit/editor-plugin-width';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

/**
 * Default extension type for Confluence. Products may override this
 * via `AgentManagedExtensionPluginOptions.extensionType`.
 */
export const AGENT_MANAGED_EXTENSION_TYPE_DEFAULT = 'com.atlassian.confluence.macro.core';
export const AGENT_MANAGED_EXTENSION_KEY = 'agent-managed-block';

export type AgentManagedObjectAttribute = {
	configVersion: string;
	id: string;
};

export type AgentManagedObjectMacroParam = {
	value?: string;
};

/**
 * Shape of the attributes we attach to the bodied extension.
 */
export type AgentManagedExtensionAttributes = {
	extensionKey?: string;
	extensionType?: string;
	layout?: string;
	localId?: string;
	parameters?: AgentManagedExtensionParameters;
};

/**
 * Shape of the `parameters` attribute we attach to the bodied extension.
 */
export type AgentManagedExtensionParameters = {
	[key: string]: unknown;
	macroParams?: {
		[key: string]: unknown;
		aiManagedObject?: AgentManagedObjectMacroParam;
	};
};

/**
 * Plugin state for the agent-managed-extension plugin.
 */
export type AgentManagedExtensionPluginState = {
	/** Cached AI-managed objects keyed by their server-issued ids. */
	aiManagedObjectsById: Record<string, AiManagedObject>;
	/** The block localId whose configuration popup is open. */
	configLocalId?: string;
	/** Blocks whose latest local execution failed without usable AIMO metadata. */
	executionErrorLocalIds: Record<string, true>;
	/** Active transient execution lifecycle and request id keyed by block localId. */
	executionLifecycleByLocalId: Record<
		string,
		{
			requestId: string;
			state: AgentManagedBlockLifecycleState;
		}
	>;
	/** Whether the inline configuration popup is currently expanded. */
	isConfigOpen: boolean;
	/**
	 * The localId of a newly inserted block whose config should open once
	 * the editor view has reconciled the node.
	 */
	pendingAutoOpenLocalId?: string;
};

export type AgentManagedBlockLifecycleState =
	| 'execution-error'
	| 'generated'
	| 'idle'
	| 'no-body-executing'
	| 'existing-body-executing';

/**
 * Plugin options.
 */
export type AgentManagedExtensionPluginOptions = {
	/**
	 * Site cloud ID used with the editor context identifier to create AI-managed objects.
	 */
	cloudId?: string;
	/**
	 * Confluence content type for the editor context identifier object ID.
	 *
	 * Defaults to `page`.
	 */
	contentType?: string;
	/**
	 * The `extensionType` attribute written into the bodied-extension ADF
	 * node. Each product's macro system uses a different namespace:
	 *
	 * - Confluence: `'com.atlassian.confluence.macro.core'`
	 * - Jira: TBD
	 *
	 * Defaults to `AGENT_MANAGED_EXTENSION_TYPE_DEFAULT` (Confluence).
	 */
	extensionType?: string;
};

export type AgentManagedExtensionPluginActions = {
	/** Cache a full AI-managed object by id in plugin state. */
	cacheAiManagedObject: (aiManagedObject: AiManagedObject) => void;
	/** Close the configure popup (regardless of which block is selected). */
	closeConfig: () => void;
	/** Open the configure popup for the agent-managed block at the selection. */
	openConfig: () => void;
	/** Toggle the configure popup for the agent-managed block at the selection. */
	toggleConfig: () => void;
};

export type AgentManagedExtensionPluginDependencies = [
	OptionalPlugin<AnalyticsPlugin>,
	OptionalPlugin<ContextIdentifierPlugin>,
	OptionalPlugin<DecorationsPlugin>,
	OptionalPlugin<FloatingToolbarPlugin>,
	OptionalPlugin<SelectionPlugin>,
	OptionalPlugin<WidthPlugin>,
];

export type AgentManagedExtensionPlugin = NextEditorPlugin<
	'agentManagedExtension',
	{
		actions: AgentManagedExtensionPluginActions;
		dependencies: AgentManagedExtensionPluginDependencies;
		pluginConfiguration: AgentManagedExtensionPluginOptions;
		sharedState: AgentManagedExtensionPluginState | undefined;
	}
>;

export const pluginKey: PluginKey<AgentManagedExtensionPluginState> =
	new PluginKey<AgentManagedExtensionPluginState>('agentManagedExtensionPlugin');
